import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useVisitorManagementEnabled } from './useFeature';
import {
  getPreCheckIns,
  createPreCheckIn,
  updatePreCheckIn,
  getCarRiderQueue,
  joinCarRiderQueue,
  updateCarRiderQueue,
  getPickupDelegations,
  createPickupDelegation,
  updatePickupDelegation,
  deletePickupDelegation,
  getStudentCheckEvents,
  createStudentCheckEvent,
  updateStudentCheckEvent,
  createTardySlip,
  type PreCheckIn,
  type CarRiderQueueEntry,
  type PickupDelegation,
  type StudentCheckEvent,
  type TardySlip,
} from '../services/visitorManagement';

export { useVisitorManagementEnabled };

export function usePreCheckIns() {
  return useQuery({
    queryKey: ['pre-checkins'],
    queryFn: getPreCheckIns,
    staleTime: 30_000,
  });
}

export function useCreatePreCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPreCheckIn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pre-checkins'] });
    },
  });
}

export function useUpdatePreCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ public_id, data }: { public_id: string; data: Partial<PreCheckIn> }) =>
      updatePreCheckIn(public_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pre-checkins'] });
    },
  });
}

export function useCarRiderQueue() {
  return useQuery({
    queryKey: ['car-rider-queue'],
    queryFn: getCarRiderQueue,
    staleTime: 15_000,
    refetchInterval: 15_000, // Keep queue real-time
  });
}

export function useJoinCarRiderQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: joinCarRiderQueue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['car-rider-queue'] });
    },
  });
}

export function useUpdateCarRiderQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      public_id,
      data,
    }: {
      public_id: string;
      data: Partial<CarRiderQueueEntry>;
    }) => updateCarRiderQueue(public_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['car-rider-queue'] });
    },
  });
}

export function usePickupDelegations() {
  return useQuery({
    queryKey: ['pickup-delegations'],
    queryFn: getPickupDelegations,
    staleTime: 30_000,
  });
}

export function useCreatePickupDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPickupDelegation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickup-delegations'] });
    },
  });
}

export function useUpdatePickupDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      public_id,
      data,
    }: {
      public_id: string;
      data: Partial<PickupDelegation>;
    }) => updatePickupDelegation(public_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickup-delegations'] });
    },
  });
}

export function useDeletePickupDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePickupDelegation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickup-delegations'] });
    },
  });
}

export function useStudentCheckEvents() {
  return useQuery({
    queryKey: ['student-check-events'],
    queryFn: getStudentCheckEvents,
    staleTime: 15_000,
    refetchInterval: 15_000, // Keep check-in history updated
  });
}

export function useCreateStudentCheckEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createStudentCheckEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-check-events'] });
    },
  });
}

export function useUpdateStudentCheckEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      public_id,
      data,
    }: {
      public_id: string;
      data: Partial<StudentCheckEvent>;
    }) => updateStudentCheckEvent(public_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-check-events'] });
    },
  });
}

export function useCreateTardySlip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      event_public_id,
      data,
    }: {
      event_public_id: string;
      data: { class_period: string };
    }) => createTardySlip(event_public_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-check-events'] });
    },
  });
}

